/**
 * Langues choisies.
 *
 * Catégories nominales, donc une seule teinte et pas de légende.
 *
 * La valeur `none` est écartée côté API : c'est le défaut de la colonne, pas
 * une langue que quelqu'un a sélectionnée. Ne restent que les changements
 * volontaires — ce qui explique des volumes bien plus faibles que le nombre
 * d'utilisateurs.
 */

import {
  Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { formatCompact, formatInteger, formatPercent } from '../lib/format';
import { AXIS_STYLE, BAR_MAX_SIZE, COLORS } from '../lib/theme';
import type { DataColumn, LanguageItem } from '../types';
import { ChartCard } from './Card';
import { ChartTooltip } from './ChartTooltip';

const columns: ReadonlyArray<DataColumn<LanguageItem>> = [
  { key: 'label', header: 'Langue', render: (row) => row.label },
  { key: 'changes', header: 'Changements', numeric: true, render: (row) => formatInteger(row.changes) },
  { key: 'share', header: 'Part', numeric: true, render: (row) => formatPercent(row.share) },
];

export function LanguageChart({ data }: { data: readonly LanguageItem[] }) {
  const height = Math.max(200, data.length * 34 + 40);

  return (
    <ChartCard
      title="Langues choisies"
      subtitle="Changements volontaires uniquement — la langue par défaut n'est pas comptée"
      emptyMessage="Aucun changement de langue sur cette période."
      rows={data}
      columns={columns}
      chartHeight={height}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data as LanguageItem[]} layout="vertical" margin={{ top: 4, right: 56, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} horizontal={false} />
          <XAxis
            type="number"
            tickFormatter={formatCompact}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="label"
            tick={{ fill: COLORS.textSecondary, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
            width={110}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            content={<ChartTooltip valueFormatter={formatInteger} />}
          />
          <Bar
            isAnimationActive={false}
            dataKey="changes"
            name="Changements"
            fill={COLORS.series1}
            radius={[0, 4, 4, 0]}
            maxBarSize={BAR_MAX_SIZE}
          >
            <LabelList
              dataKey="changes"
              position="right"
              fill={COLORS.textSecondary}
              fontSize={12}
              formatter={(value: unknown) => formatInteger(Number(value))}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
